// SHACL runner — wraps Oxigraph's SPARQL-driven shape evaluation.
//
// Note (sub-step 6 scope):
//   Oxigraph 0.5.x does not yet ship a built-in SHACL engine. Phase 2a
//   strategy (per WASM doc §10) is to either (a) translate SHACL shapes to
//   SPARQL queries at build time and run them through Oxigraph's query API,
//   or (b) wait for Oxigraph SHACL support upstream.
//
//   For Step 3 we expose the SPARQL-fallback shape: `applyShacl()` accepts
//   a list of shape-derived SPARQL ASK/SELECT queries + a target IRI, runs
//   them against the in-memory store, and returns a structured report.
//   The signature is locked so Studio integration (Step 4) can swap in a
//   true SHACL engine without breaking callers.

import { getStore } from "./core-loader"

export interface ShaclQueryRule {
  /** Shape IRI (cogo:LayoutShape, cogo:NavMenuShape, etc.) */
  shape: string
  /** Human-readable focus class name (e.g. "Navigation"). */
  focusClass?: string
  /**
   * SPARQL ASK or SELECT body. ASK returns a single boolean; SELECT returns
   * the violating bindings (each binding is treated as one violation).
   */
  sparql: string
  /** True if this is an ASK query (asserted-true means PASS). */
  ask: boolean
  /** Severity (matches sh:Violation / sh:Warning / sh:Info). */
  severity: "violation" | "warning" | "info"
  /** Human-readable message template (filled by caller post-hoc). */
  message?: string
}

export interface ShaclViolation {
  shape: string
  severity: "violation" | "warning" | "info"
  focusClass?: string
  message?: string
  /** Bound variable values from the SELECT row (empty for ASK rules). */
  bindings: Record<string, string>
}

export interface ShaclReport {
  conforms: boolean
  totalRules: number
  violations: ShaclViolation[]
  durationMs: number
}

/**
 * Run a set of SHACL-derived SPARQL rules against the loaded core graph.
 * Returns a structured report mirroring the SHACL Conforms / Violations
 * output format.
 */
export function applyShacl(rules: ShaclQueryRule[]): ShaclReport {
  const store = getStore()
  if (!store) {
    throw new Error(
      "[cogo-kg-browser] applyShacl called before initKgCore — store not ready.",
    )
  }
  const t0 = nowMs()
  const violations: ShaclViolation[] = []

  for (const rule of rules) {
    try {
      const out = store.query(rule.sparql)
      if (rule.ask) {
        // ASK returns a boolean. Per SHACL convention, an ASK that returns
        // true means the constraint IS asserted; if the rule is phrased as
        // a forbidden pattern, true == violation. We don't try to invert
        // here — caller defines the polarity in `message`.
        if (out === true) {
          violations.push({
            shape: rule.shape,
            severity: rule.severity,
            focusClass: rule.focusClass,
            message: rule.message,
            bindings: {},
          })
        }
      } else {
        // SELECT — each row is a violation.
        const rows = out as Iterable<Map<string, unknown>>
        for (const row of rows) {
          const bindings: Record<string, string> = {}
          if (row && typeof (row as any)[Symbol.iterator] === "function") {
            for (const [k, v] of row as unknown as Iterable<[string, { value?: string } | null]>) {
              bindings[k] = v?.value ?? "null"
            }
          }
          violations.push({
            shape: rule.shape,
            severity: rule.severity,
            focusClass: rule.focusClass,
            message: rule.message,
            bindings,
          })
        }
      }
    } catch (e) {
      violations.push({
        shape: rule.shape,
        severity: "violation",
        focusClass: rule.focusClass,
        message: `SPARQL evaluation failed: ${(e as Error).message}`,
        bindings: {},
      })
    }
  }

  const conforms = !violations.some((v) => v.severity === "violation")
  return {
    conforms,
    totalRules: rules.length,
    violations,
    durationMs: nowMs() - t0,
  }
}

function nowMs(): number {
  const perf = (globalThis as any).performance
  if (perf && typeof perf.now === "function") return perf.now()
  return Date.now()
}
